import { useEffect, useState } from "react";
import { isAxiosError } from "axios";
import DOMPurify from "dompurify";
import { TListener } from "@/types";
import { PrizeCompetition } from "@/types/lottery.ts";
import { getLatestPrizeCompetition } from "@/api/getLatestPrizeCompetition.ts";
import { useUser } from "@/hooks/useUser.ts";
import { useClient } from "@/hooks/useClient.ts";

type PrizeCompetitionState = {
  competition: PrizeCompetition | null;
  // Sanitized claim instruction HTML, or null when there is nothing to show
  claimInstruction: string | null;
  loading: boolean;
  error: boolean;
};

const initialState: PrizeCompetitionState = {
  competition: null,
  claimInstruction: null,
  loading: true,
  error: false,
};

const disabledState: PrizeCompetitionState = {
  ...initialState,
  loading: false,
};

let _state = initialState;
let _request: Promise<void> | null = null;
let listeners: TListener<PrizeCompetitionState>[] = [];

const setState = (state: PrizeCompetitionState) => {
  _state = state;
  listeners.forEach((listener) => listener(_state));
};

// Treats null, "" and empty editor markup such as <p><br></p> as no instruction
const sanitizeClaimInstruction = (html: string | null) => {
  if (!html) return null;
  const body = DOMPurify.sanitize(html, { RETURN_DOM: true }) as HTMLElement;
  return body.textContent?.trim() ? body.innerHTML : null;
};

const fetchPrizeCompetition = async () => {
  setState({ ..._state, loading: true });
  try {
    const response = await getLatestPrizeCompetition();
    setState({
      competition: response.data,
      claimInstruction: sanitizeClaimInstruction(
        response.data.free_ticket_claim_instruction,
      ),
      loading: false,
      error: false,
    });
  } catch (error: unknown) {
    // 404 means there is no active prize competition, which is a normal state
    const status = isAxiosError(error) ? error.response?.status : undefined;
    setState({
      ...disabledState,
      error: status !== 404 && status !== 403,
    });
  }
};

export const refreshPrizeCompetition = () => {
  if (!_request) {
    _request = fetchPrizeCompetition().finally(() => {
      _request = null;
    });
  }
  return _request;
};

export const usePrizeCompetition = () => {
  const user = useUser();
  const client = useClient();
  const enabled = !!user && !!client?.prize_competition_mode;
  const [state, setLocalState] = useState(_state);

  useEffect(() => {
    listeners.push(setLocalState);
    setLocalState(_state);
    return () => {
      listeners = listeners.filter((listener) => listener !== setLocalState);
    };
  }, []);

  useEffect(() => {
    if (enabled) {
      refreshPrizeCompetition();
    } else if (_state !== initialState) {
      setState(initialState);
    }
  }, [enabled, user?.id]);

  return enabled ? state : disabledState;
};
